/**
 * Rende visibili gli avvisi che arrivano sulle scritture riuscite (`meta.warnings`).
 *
 * La raccolta è nel client JSON:API (`shared/api/jsonapi/warnings.ts`); qui c'è la resa, che è
 * l'unica parte che riguarda l'utente. Sta separata perché il layer HTTP non deve sapere cosa
 * sia una notifica, e perché così il test del client può verificare la raccolta senza montare
 * Mantine.
 *
 * ## La regola sul testo
 *
 * Il messaggio del BE, quando c'è, **vince** sul nostro: è quello che conosce il caso
 * specifico. Il catalogo serve per i codici che arrivano nudi — ed è il caso di
 * `ORDER_NEXT_PROJECT_TEAM_COPY_FAILED`, che senza una nostra frase comparirebbe a schermo
 * come il suo codice in maiuscolo.
 *
 * Un avviso di cui non sappiamo dire nulla si mostra lo stesso, con una frase e il codice come
 * riferimento: tacere un segnale che il server ha ritenuto di mandare è peggio di mostrarlo in
 * forma grezza — chi lo legge può almeno riferirlo.
 */
import { messageForWarning } from '@/shared/api/jsonapi/errors';
import { setJsonApiWarningHandler, type JsonApiWarning, type WarningContext } from '@/shared/api/jsonapi/warnings';
import { notifyWarn } from '@/shared/lib/notify';

/**
 * Testo da mostrare per un avviso. La regola sta in `messageForWarning` (`errors.ts`), che è la
 * stessa per le scritture JSON:API e per le azioni di workflow: builder coi dati del caso →
 * messaggio del BE → catalogo avvisi → catalogo errori → frase di riserva col codice come rif.
 */
export function warningMessage(w: JsonApiWarning): string {
  return messageForWarning(w);
}

/**
 * Aggancia il riferimento del caso, quando c'è.
 *
 * `salesOrderLineId` non è decorativo: è ciò che permette di ritrovare *quale* riga d'ordine
 * ha lasciato la commessa successiva senza team, in un mese in cui se ne chiudono decine.
 */
export function warningDetail(w: JsonApiWarning): string | null {
  const p = w.params ?? {};
  const ref = p.salesOrderLineId ?? p.projectId ?? p.id;
  return typeof ref === 'string' && ref ? `Riferimento: ${ref}` : null;
}

/**
 * Registra la resa degli avvisi. Da chiamare una volta all'avvio dell'app.
 *
 * Restituisce la funzione di disinstallazione, così un `useEffect` può ripulire e i test
 * possono montare e smontare senza lasciare l'handler acceso fra un caso e l'altro.
 */
export function installJsonApiWarningNotifier(): () => void {
  setJsonApiWarningHandler((warnings: JsonApiWarning[], ctx: WarningContext) => {
    for (const w of warnings) {
      const detail = warningDetail(w);
      notifyWarn(
        detail ? `${warningMessage(w)} ${detail}` : warningMessage(w),
        'Salvato, con un’avvertenza',
      );
    }
    // Il contesto non entra nel testo: all'utente interessa cosa è successo, non su quale
    // risorsa REST. Resta nel tipo perché un handler diverso (una pagina che vuole mostrarlo
    // in linea invece che come toast) ne ha bisogno.
    void ctx;
  });
  return () => setJsonApiWarningHandler(null);
}
